import React, { Component } from 'react';
import {
  Layout, Row, Col, Table, Input, Button, Tag, Alert, notification, Icon,
} from 'antd';
import { adminInstitutionSettingsService } from '../../services';

const { Content } = Layout;

// Internal administration (AD) screen: lists the whole institution_settings
// catalogue so we can see which configs exist and maintain their default_value,
// which /institutions/settings serves when an institution has no value of its own.
class AdminInstitutionSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      saving: {},
      settings: [],
      edits: {},
      query: '',
    };
  }

  componentDidMount() {
    this.load();
  }

  getErrorMessage = (error, fallback) => {
    const data = error && error.data ? error.data : null;
    if (typeof data === 'string') return data;
    return (data && data.message) || fallback;
  };

  getEditedValue = (record) => {
    const { edits } = this.state;
    if (edits[record.id] !== undefined) return edits[record.id];
    return record.default_value === null || record.default_value === undefined
      ? ''
      : record.default_value;
  };

  isDirty = (record) => {
    const current = record.default_value === null || record.default_value === undefined
      ? ''
      : record.default_value;
    return this.getEditedValue(record) !== current;
  };

  load = () => {
    this.setState({ loading: true });
    adminInstitutionSettingsService
      .getInstitutionSettings()
      .then((settings) => {
        this.setState({ settings: settings || [], edits: {}, loading: false });
      })
      .catch((error) => {
        this.setState({ loading: false });
        notification.error({
          message: this.getErrorMessage(error, 'Falha ao carregar as configurações'),
        });
      });
  };

  handleSearchChange = (e) => {
    this.setState({ query: e.target.value });
  };

  handleValueChange = (record, value) => {
    this.setState((prevState) => ({
      edits: { ...prevState.edits, [record.id]: value },
    }));
  };

  handleSave = (record) => {
    const value = this.getEditedValue(record);

    this.setState((prevState) => ({ saving: { ...prevState.saving, [record.id]: true } }));

    adminInstitutionSettingsService
      .updateDefaultValue(record.id, value)
      .then((updated) => {
        this.setState((prevState) => {
          const settings = prevState.settings.map((setting) => {
            if (setting.id !== record.id) return setting;
            return { ...setting, ...updated };
          });
          const edits = { ...prevState.edits };
          delete edits[record.id];
          const saving = { ...prevState.saving };
          delete saving[record.id];
          return { settings, edits, saving };
        });
        notification.success({
          message: `Valor padrão da configuração ${record.id} atualizado`,
        });
      })
      .catch((error) => {
        this.setState((prevState) => {
          const saving = { ...prevState.saving };
          delete saving[record.id];
          return { saving };
        });
        notification.error({
          message: this.getErrorMessage(error, 'Falha ao atualizar o valor padrão'),
        });
      });
  };

  render() {
    const {
      settings, loading, query, saving,
    } = this.state;

    const q = (query || '').toLowerCase();
    const filteredSettings = q
      ? settings.filter((setting) => {
        const id = String(setting.id);
        const name = (setting.name || '').toLowerCase();
        const description = (setting.description || '').toLowerCase();
        return id.includes(q) || name.includes(q) || description.includes(q);
      })
      : settings;

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: 'Nome',
        dataIndex: 'name',
        key: 'name',
        render: (name) => name || '-',
      },
      {
        title: 'Descrição',
        dataIndex: 'description',
        key: 'description',
        render: (description) => description || '-',
      },
      {
        title: 'Instituições',
        dataIndex: 'institutions_count',
        key: 'institutions_count',
        width: 120,
        render: (count) => (
          <Tag color={count ? '#1bbfbb' : ''}>{count || 0}</Tag>
        ),
      },
      {
        title: 'Valor padrão',
        key: 'default_value',
        width: 320,
        render: (text, record) => (
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              style={{ width: 180 }}
              placeholder="Sem valor padrão"
              value={this.getEditedValue(record)}
              onChange={(e) => this.handleValueChange(record, e.target.value)}
              onPressEnter={() => this.isDirty(record) && this.handleSave(record)}
              disabled={!!saving[record.id]}
            />
            <Button
              type="primary"
              style={{ width: 88 }}
              disabled={!this.isDirty(record) || !!saving[record.id]}
              onClick={() => this.handleSave(record)}
            >
              {saving[record.id] ? <Icon type="loading" spin /> : 'Salvar'}
            </Button>
          </div>
        ),
      },
    ];

    return (
      <Content>
        <Row>
          <Col xs={24} lg={12}>
            <h1 className="title">Configurações institucionais (AD)</h1>
          </Col>
          <Col xs={24} lg={12}>
            <div style={{ marginBottom: 10 }}>
              <Input.Search
                placeholder="Buscar por id, nome ou descrição"
                value={query}
                onChange={this.handleSearchChange}
                allowClear
                style={{ width: '100%' }}
                disabled={loading}
              />
            </div>
          </Col>
          <Col span={24}>
            <Alert
              style={{ marginBottom: 16 }}
              type="info"
              showIcon
              message="Valor padrão (default_value)"
              description={
                'É o valor devolvido por /institutions/settings quando a instituição não tem '
                + 'a configuração cadastrada. Deixe em branco para que a configuração continue '
                + 'inexistente para quem não a cadastrou. A coluna "Instituições" mostra quantas '
                + 'instituições já têm valor próprio (o valor próprio sempre prevalece).'
              }
            />
          </Col>
          <Col span={24}>
            <Table
              rowKey={(record) => record.id}
              loading={loading}
              dataSource={filteredSettings}
              columns={columns}
              pagination={false}
            />
          </Col>
        </Row>
      </Content>
    );
  }
}

export default AdminInstitutionSettings;
